// automationEditor.js — page-level automation composer
//
// Reuses ChannelPushForm + DeepLinkPicker from the campaigns module so push payload
// editing is identical between campaigns and automations.

import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Button, Card, CardBody, Col, FormGroup, Input, Label, Row,
} from 'reactstrap';
import { firestore, functions } from '../../../../helpers/Firebase';
import ChannelPushForm from '../campaigns/components/ChannelPushForm.jsx';
import SegmentPicker from '../campaigns/components/SegmentPicker.jsx';
// TriggerForm removed on 2026-08-01 — automations now fire purely from
// their ruleTree (audience filter), see marketingAutomationsTick.
import ReachPanel from '../segments/components/ReachPanel.jsx';
import { actorFromUser, computeDiff, writeAuditEntry } from '../shared/auditLog.js';
import DynamicFields from '../shared/DynamicFields.jsx';
import { getRoute } from '../campaigns/helpers/routeCatalog.js';

// Pre-fill from settings/marketing.testUserIds[0] if the caller hasn't yet
// typed a UID — one less step for the "test on the seed test user" case.
const useDefaultTestUid = () => {
  const [uid, setUid] = useState('');
  useEffect(() => {
    let cancelled = false;
    firestore().collection('settings').doc('marketing').get().then(snap => {
      if (cancelled) return;
      const list = snap.exists ? snap.data().testUserIds : null;
      if (list && list.length) setUid(list[0]);
    }).catch(() => null);
    return () => { cancelled = true; };
  }, []);
  return [uid, setUid];
};

const emptyAutomation = () => ({
  name: '',
  description: '',
  active: false,
  // trigger removed on 2026-08-01 — automations fire from ruleTree via
  // marketingAutomationsTick (5-min pubsub cron).
  campaignTemplate: {
    channels: ['push'],
    payload: {
      push: {
        localized: { en: {}, ar: {} },
        deepLink: { route: 'home', params: {} },
      },
    },
  },
  frequencyCap: { perUserDays: 7 },
  stats: { runs: 0, totalSent: 0, totalOpened: 0 },
});

const AutomationEditor = ({ match, history }) => {
  const automationId = match.params.id || null;
  const isNew = !automationId;
  const currentUser = useSelector(s => s.authUser?.user);

  const [automation, setAutomation] = useState(emptyAutomation());
  const [originalAutomation, setOriginalAutomation] = useState(null); // snapshot for audit-diff
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await firestore().collection('marketing_automations').doc(automationId).get();
        if (cancelled) return;
        if (!snap.exists) setError('Automation not found');
        else {
          const loaded = { ...emptyAutomation(), ...snap.data() };
          setAutomation(loaded);
          setOriginalAutomation(loaded); // baseline for audit diff on edit
        }
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [automationId, isNew]);

  const update = (patch) => setAutomation({ ...automation, ...patch });

  // Split into hard errors (block save) and soft warnings (informational).
  // Marketing can save with warnings; they can't save with errors.
  const validate = () => {
    const errors = [];
    const warnings = [];
    if (!automation.name?.trim()) errors.push('Name is required');
    const en = automation.campaignTemplate?.payload?.push?.localized?.en || {};
    const ar = automation.campaignTemplate?.payload?.push?.localized?.ar || {};
    if (!en.title || !en.body) errors.push('English title and body are required');

    // Non-blocking: server falls back to EN when AR is missing (see helpers.js
    // dispatch: `localized[lang] || localized.en`). Users with language='ar'
    // still get the notification — just in English.
    if ((en.title || en.body) && (!ar.title || !ar.body)) {
      warnings.push('Arabic title/body missing — users with language=ar will receive the English copy.');
    }

    // Required deep-link params (e.g. service picker on route: 'service').
    // Params marked `autoFill` are resolved server-side at fire time — skip.
    const dl = automation.campaignTemplate?.payload?.push?.deepLink;
    const routeDef = dl?.route && getRoute(dl.route);
    if (routeDef) {
      for (const p of routeDef.params || []) {
        if (p.autoFill) continue;
        if (p.required && !dl.params?.[p.key]) {
          errors.push(`Tap destination requires "${p.label}"`);
        }
      }
    }
    // Trigger validation removed on 2026-08-01. Automations fire purely from
    // ruleTree — enforce the audience is meaningful instead.
    const rules = automation.ruleTree?.rules || [];
    if (!rules.length) {
      errors.push('Audience is empty — the automation will never fire. Add at least one rule.');
    }
    if (automation.active && !rules.length) {
      warnings.push('Automation is active but the audience has no rules — nothing will fire.');
    }
    return { errors, warnings };
  };

  const { errors, warnings } = validate();
  const canSave = errors.length === 0 && !saving;

  const onSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...automation,
        updatedAt: Date.now(),
        updatedBy: currentUser?.uid || null,
        updatedByName: currentUser?.displayName || currentUser?.email || null,
      };
      let savedId = automationId;
      if (isNew) {
        payload.createdAt = Date.now();
        payload.createdBy = currentUser?.uid || null;
        payload.createdByName = currentUser?.displayName || currentUser?.email || null;
        const ref = await firestore().collection('marketing_automations').add(payload);
        savedId = ref.id;
        history.replace(`/app/marketing/automations/${ref.id}/edit`);
      } else {
        await firestore().collection('marketing_automations').doc(automationId).set(payload, { merge: true });
      }

      // Audit log — fire-and-forget so a bookkeeping failure never breaks save
      const actor = actorFromUser(currentUser);
      if (isNew) {
        writeAuditEntry('marketing_automations', savedId, { ...actor, action: 'created' });
      } else if (originalAutomation) {
        const changes = computeDiff(originalAutomation, payload);
        // Emit a granular activate/deactivate entry alongside any edit
        if ('active' in changes) {
          writeAuditEntry('marketing_automations', savedId, {
            ...actor,
            action: changes.active.to ? 'active' : 'inactive',
          });
        }
        if (Object.keys(changes).length > 0) {
          writeAuditEntry('marketing_automations', savedId, { ...actor, action: 'edited', changes });
        }
        setOriginalAutomation(payload); // refresh baseline for future diffs in the same session
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading" />;

  return (
    <>
      {/* Page header — title left, action buttons right (ListPageHeading pattern) */}
      <Row className="mb-3">
        <Col xs="12" md="6">
          <h1 className="mb-0 p-0">{isNew ? 'New automation' : 'Edit automation'}</h1>
        </Col>
        <Col xs="12" md="6">
          <div className="d-flex align-items-center justify-content-end" style={{ gap: 8 }}>
            <Button
              color="danger" outline size="lg"
              onClick={() => history.push('/app/marketing/automations')}
            >
              Dismiss
            </Button>
            <Button
              color="primary" size="lg"
              disabled={!canSave}
              onClick={onSave}
            >
              {saving ? 'Saving…' : (isNew ? 'Create automation' : 'Save changes')}
            </Button>
          </div>
          {error && <div className="text-danger small text-right mt-2">Error: {error}</div>}
        </Col>
      </Row>

    <Row>
      <Col xs="12" md="8">
        <Card className="mb-3">
          <CardBody>
            <DynamicFields
              value={automation}
              onChange={setAutomation}
              elements={[
                { key: 'name', title: 'Name', type: 'text', width: '12', required: true, maxLength: 100, placeholder: 'e.g. Welcome series — Day 7' },
                { key: 'description', title: 'Description (internal)', type: 'textarea', width: '12', rows: 2, placeholder: 'What this automation does + why' },
                // "Active" toggle intentionally NOT here — it lives on the
                // list row (automationsList) so marketing can enable/disable
                // without opening the editor. Matches the providers/washers
                // pattern where the active status is a row-level switch.
              ]}
            />
          </CardBody>
        </Card>

        <Card className="mb-3">
          <CardBody>
            <h6 className="mb-3">Audience</h6>
            <p className="text-muted small mb-3">
              Pick who this automation fires for. Every 5 minutes the marketingAutomationsTick
              cron scans users matching this filter and sends the notification (respecting the
              per-user frequency cap below).
            </p>
            <SegmentPicker
              value={{
                segmentId: automation.segmentId || null,
                segmentName: automation.segmentName || null,
                ruleTree: automation.ruleTree || null,
              }}
              onChange={(v) => update({
                segmentId: v.segmentId || null,
                segmentName: v.segmentName || null,
                ruleTree: v.ruleTree || null,
              })}
            />
          </CardBody>
        </Card>

        <Card className="mb-3">
          <CardBody>
            <h6 className="mb-3">Notification</h6>
            <ChannelPushForm
              value={automation.campaignTemplate.payload.push}
              onChange={(v) => update({
                campaignTemplate: {
                  ...automation.campaignTemplate,
                  payload: { ...automation.campaignTemplate.payload, push: v },
                },
              })}
              context="automation"
            />
          </CardBody>
        </Card>

        <Card className="mb-3">
          <CardBody>
            <h6 className="mb-3">Frequency cap</h6>
            <DynamicFields
              value={automation}
              onChange={setAutomation}
              elements={[
                {
                  key: 'frequencyCap.perUserDays',
                  dotPath: true,
                  title: 'Per-user cool-down (days)',
                  type: 'number',
                  width: '6',
                  min: 0,
                  hint: 'Each user can receive this automation at most once every N days. 0 = no cap.',
                },
              ]}
            />
          </CardBody>
        </Card>

        {(errors.length > 0 || warnings.length > 0) && (
          <Card className="mb-3">
            <CardBody>
              {errors.length > 0 && (
                <div className="text-danger mb-2">
                  <small>
                    <strong>Fix before saving:</strong>
                    <ul className="mb-0 mt-1 pl-3">
                      {errors.slice(0, 5).map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  </small>
                </div>
              )}
              {warnings.length > 0 && (
                <div className="text-warning mb-0">
                  <small>
                    <strong>Heads up (won't block save):</strong>
                    <ul className="mb-0 mt-1 pl-3">
                      {warnings.slice(0, 5).map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                  </small>
                </div>
              )}
            </CardBody>
          </Card>
        )}
      </Col>

      <Col xs="12" md="4">
        {/* Segment reach for the audience filter. Does NOT reflect actual fire
            volume — the trigger will fire for a subset of the matched segment
            (e.g. schedule triggers only fire for users who hit the anchor+offset
            on the given day). Shown here so marketing can sanity-check the
            audience filter size before enabling the automation. */}
        <ReachPanel
          ruleTree={automation.ruleTree}
          title="Audience filter reach"
        />
        <div className="text-muted small mb-3" style={{ marginTop: -8 }}>
          Segment size only. Actual per-fire volume depends on the trigger — a
          schedule/inactivity trigger only fires for the subset of users hitting
          the anchor on a given day.
        </div>

        <Card className="mb-3">
          <CardBody>
            <h6 className="mb-2">Stats</h6>
            <div className="small text-muted">
              <div>Runs: <strong>{automation.stats?.runs || 0}</strong></div>
              <div>Total sent: <strong>{automation.stats?.totalSent || 0}</strong></div>
              <div>Total opened: <strong>{automation.stats?.totalOpened || 0}</strong></div>
            </div>
            <hr />
            <small className="text-muted">
              <strong>Test-user-first:</strong> during build phase the trigger only fires for the
              designated test user. Production users get profiles only at Rollout cutover.
            </small>
          </CardBody>
        </Card>

        <TestFireCard automationId={automationId} isNew={isNew} />
      </Col>
    </Row>
    </>
  );
};

// Sidebar card that lets marketing smoke-test the automation against one or
// more recipients. Accepts phone numbers (with country code, +971…) or UIDs,
// comma- or newline-separated. Bypasses freq caps, segment filter, and
// transactional-collision guards (server-side, via testFireAutomation callable).
// Only visible on saved automations — a draft can't be tested until it has a docId.
const TestFireCard = ({ automationId, isNew }) => {
  const [recipientsInput, setRecipientsInput] = useDefaultTestUid();
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const currentUser = useSelector(s => s.authUser?.user);

  if (isNew) {
    return (
      <Card>
        <CardBody>
          <h6 className="mb-2">Test fire</h6>
          <small className="text-muted">Save the automation first, then come back here to send a test push to any user.</small>
        </CardBody>
      </Card>
    );
  }

  const onSend = async () => {
    if (!recipientsInput.trim()) {
      setResult({ type: 'error', message: 'At least one recipient is required' });
      return;
    }
    setSending(true);
    setResult(null);
    try {
      const recipients = recipientsInput.split(/[\n,]/).map(s => s.trim()).filter(Boolean);
      const callable = functions().httpsCallable('testFireAutomation');
      const resp = await callable({ automationId, recipients });
      const data = resp.data || {};
      const type = (data.sent || 0) > 0 ? 'success' : 'error';
      setResult({
        type,
        message: `${data.sent || 0} sent · ${data.failed || 0} failed · ${data.skipped || 0} skipped`,
      });
      // One audit entry per test-fire click, summarizing the batch
      writeAuditEntry('marketing_automations', automationId, {
        ...actorFromUser(currentUser),
        action: 'test-fired',
        notes: `${recipients.length} recipient(s) · sent=${data.sent || 0}, failed=${data.failed || 0}, skipped=${data.skipped || 0}`,
      });
    } catch (e) {
      setResult({ type: 'error', message: e.message });
    } finally {
      setSending(false);
    }
  };

  return (
    <Card>
      <CardBody>
        <h6 className="mb-2">Test fire</h6>
        <small className="text-muted d-block mb-2">
          Sends a real push to one or more users, bypassing frequency caps and segment filters.
          Phone numbers (with country code) or user UIDs, comma- or newline-separated. Max 20.
        </small>
        <FormGroup>
          <Label size="sm">Recipients</Label>
          <Input
            bsSize="sm"
            type="textarea" rows={2}
            value={recipientsInput}
            onChange={(e) => setRecipientsInput(e.target.value)}
            placeholder="+971111111118, anotherUid, +9715xxx…"
          />
        </FormGroup>
        <Button color="primary" size="sm" disabled={sending || !recipientsInput.trim()} onClick={onSend}>
          {sending ? 'Sending…' : 'Send test push'}
        </Button>
        {result && (
          <div className={`mt-2 small ${result.type === 'success' ? 'text-success' : 'text-danger'}`}>
            {result.message}
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default AutomationEditor;
